import { create } from 'zustand';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'repair-app-theme';

interface ThemeState {
  theme: Theme;
  toggleTheme: () => void;
  initTheme: () => void;
  setTheme: (theme: Theme) => void;
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: 'light',

  initTheme: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
      if (stored === 'light' || stored === 'dark') {
        applyTheme(stored);
        set({ theme: stored });
      } else {
        // Respect system preference on first load
        const prefersDark = window.matchMedia(
          '(prefers-color-scheme: dark)',
        ).matches;
        const systemTheme: Theme = prefersDark ? 'dark' : 'light';
        applyTheme(systemTheme);
        set({ theme: systemTheme });
      }
    } catch {
      // Fallback to light if localStorage is unavailable
      applyTheme('light');
      set({ theme: 'light' });
    }
  },

  setTheme: (theme: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Ignore storage errors
    }
    applyTheme(theme);
    set({ theme });
  },

  toggleTheme: () => {
    const current = get().theme;
    const next: Theme = current === 'light' ? 'dark' : 'light';
    get().setTheme(next);
  },
}));
